import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navigation from './components/Navigation';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';
import ForcePasswordChange from './components/ForcePasswordChange';
import EnvironmentIndicator from './components/EnvironmentIndicator';
import { ErrorBoundary } from './components/ErrorBoundary';
import environment from './config/environment';
import { Menu } from 'lucide-react';

// Import all the analytics pages
import Dashboard from './pages/admin/Dashboard';
import ActivityFeed from './pages/admin/ActivityFeed';
import ActionHeatmapPage from './pages/admin/ActionHeatmapPage';
import ParkingLotsPage from './pages/admin/ParkingLotsPage';
import ParkingLotDetail from './pages/admin/ParkingLotDetail';
import FeedbackAnalytics from './pages/admin/FeedbackAnalytics';
import LiveDataControls from './pages/admin/LiveDataControls';
import ProductionTest from './pages/admin/ProductionTest';
import SessionsPage from './pages/admin/SessionsPage';
import ReportsPage from './pages/admin/ReportsPage';
import PrsHealthPage from './pages/admin/PrsHealthPage';
import ReceiptRequestsPage from './pages/admin/ReceiptRequestsPage';
import CampaignsPage from './pages/admin/CampaignsPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';

const AppContent: React.FC = () => {
    const { isAuthenticated, isLoading, user, logout } = useAuth();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isNavCollapsed, setIsNavCollapsed] = useState(() => {
        try {
            return localStorage.getItem('admin.navCollapsed') === '1';
        } catch {
            return false;
        }
    });
    const isSessionViewer = user?.role === 'session_viewer';

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const toggleNavCollapsed = () => {
        setIsNavCollapsed((v) => {
            const next = !v;
            try {
                localStorage.setItem('admin.navCollapsed', next ? '1' : '0');
            } catch {
                // ignore
            }
            return next;
        });
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Login />;
    }

    // Admin-created / password-reset accounts must set a new password before
    // they can use the rest of the panel.
    if (user?.mustChangePassword) {
        return <ForcePasswordChange />;
    }

    return (
        <Router>
            <div className="min-h-screen bg-background">
                <EnvironmentIndicator />
                {/* Header with logout button and mobile menu toggle */}
                <header className="bg-white shadow-sm border-b">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between items-center h-16">
                            <div className="flex items-center gap-4">
                                {/* Mobile menu button */}
                                <button
                                    onClick={toggleMobileMenu}
                                    className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                >
                                    <Menu className="h-6 w-6" />
                                </button>

                                <h1 className="text-lg md:text-xl font-semibold text-gray-900 truncate">
                                    {environment.appName}
                                </h1>
                            </div>

                            <div className="flex items-center space-x-2 md:space-x-4">
                                <span className="text-sm text-gray-600 hidden sm:block">
                                    Welcome, {user?.username}
                                </span>
                                <button
                                    onClick={logout}
                                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 md:px-4 md:py-2 rounded-md text-sm font-medium transition-colors"
                                >
                                    Logout
                                </button>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Main content with sidebar navigation */}
                <div className="flex h-[calc(100vh-4rem)]">
                    <Navigation
                        isMobileMenuOpen={isMobileMenuOpen}
                        onToggleMobileMenu={toggleMobileMenu}
                        isCollapsed={isNavCollapsed}
                        onToggleCollapsed={toggleNavCollapsed}
                    />
                    <main className="flex-1 overflow-auto p-4 md:p-6 w-full">
                        <ErrorBoundary>
                            <Routes>
                                <Route 
                                    path="/" 
                                    element={<Navigate to={isSessionViewer ? "/sessions" : "/dashboard"} replace />} 
                                />
                                <Route 
                                    path="/dashboard" 
                                    element={
                                        <ProtectedRoute allowedRoles={['admin', 'insights']}>
                                            <Dashboard />
                                        </ProtectedRoute>
                                    } 
                                />
                                <Route 
                                    path="/analytics/activity-feed" 
                                    element={
                                        <ProtectedRoute allowedRoles={['admin', 'insights']}>
                                            <ActivityFeed />
                                        </ProtectedRoute>
                                    } 
                                />
                                <Route
                                    path="/analytics/action-heatmap"
                                    element={
                                        <ProtectedRoute allowedRoles={['admin', 'insights']}>
                                            <ActionHeatmapPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route 
                                    path="/analytics/feedback" 
                                    element={
                                        <ProtectedRoute allowedRoles={['admin', 'insights']}>
                                            <FeedbackAnalytics />
                                        </ProtectedRoute>
                                    } 
                                />
                                <Route 
                                    path="/parking-lots" 
                                    element={
                                        <ProtectedRoute allowedRoles={['admin']}>
                                            <ParkingLotsPage />
                                        </ProtectedRoute>
                                    } 
                                />
                                <Route 
                                    path="/parking-lots/:id" 
                                    element={
                                        <ProtectedRoute allowedRoles={['admin']}>
                                            <ParkingLotDetail />
                                        </ProtectedRoute>
                                    } 
                                />
                                <Route 
                                    path="/live-data-controls" 
                                    element={
                                        <ProtectedRoute allowedRoles={['admin']}>
                                            <LiveDataControls />
                                        </ProtectedRoute>
                                    } 
                                />
                                <Route 
                                    path="/production-test" 
                                    element={
                                        <ProtectedRoute allowedRoles={['admin']}>
                                            <ProductionTest />
                                        </ProtectedRoute>
                                    } 
                                />
                                <Route 
                                    path="/receipt-requests" 
                                    element={
                                        <ProtectedRoute allowedRoles={['admin']}>
                                            <ReceiptRequestsPage />
                                        </ProtectedRoute>
                                    } 
                                />
                                <Route 
                                    path="/campaigns" 
                                    element={
                                        <ProtectedRoute allowedRoles={['admin']}>
                                            <CampaignsPage />
                                        </ProtectedRoute>
                                    } 
                                />
                                {/* Sessions and Reports are accessible to all authenticated users
                                    (the API scopes municipality accounts and redacts insights accounts) */}
                                <Route path="/sessions" element={<SessionsPage />} />
                                <Route path="/reports" element={<ReportsPage />} />
                                <Route
                                    path="/prs-health"
                                    element={
                                        <ProtectedRoute allowedRoles={['admin']}>
                                            <PrsHealthPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/team"
                                    element={
                                        <ProtectedRoute allowedRoles={['admin']}>
                                            <AdminUsersPage />
                                        </ProtectedRoute>
                                    }
                                />
                            </Routes>
                        </ErrorBoundary>
                    </main>
                </div>
            </div>
        </Router>
    );
};

function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}

export default App; 
